import { useRef, useState } from "react";
import { FormControl, Typography } from "@mui/material";
import { Controller, UseFormRegister } from "react-hook-form";
import { JiraProject } from "~/operations";
import {
  Autocomplete,
  DropdownAutocomplete,
} from "~/components/Form/components";
import { Control, FormState } from "react-hook-form/dist/types/form";
import { CreateCasesFormInput } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketDialog";
import { CreateTicketNotesField } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketNotesField";
import { CreateTicketTitleField } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketTitleField";

type CreateCaseModalProps = {
  hasCreateCasePermissions: boolean;
  register: UseFormRegister<CreateCasesFormInput>;
  control: Control<CreateCasesFormInput>;
  formState: FormState<CreateCasesFormInput>;
  isProjectsEmpty: boolean;
  projects: JiraProject[] | null | undefined;
  saveSelectedProject: (value: string) => void;
  fetchProjects: (value: string) => void;
};

export const CreateTicketJiraDialog = ({
  hasCreateCasePermissions,
  control,
  register,
  formState,
  projects,
  isProjectsEmpty,
  saveSelectedProject,
  fetchProjects,
}: CreateCaseModalProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [projectQuery, setProjectQuery] = useState("");

  const handleToggle = () => {
    setIsAutocompleteOpen((prevOpen) => !prevOpen);
  };

  return (
    <>
      <FormControl fullWidth>
        <Typography
          variant="body1"
          gutterBottom
          sx={{ mb: 1, fontWeight: 700 }}
        >
          Project
        </Typography>

        <Controller
          key="projectId"
          name="jira.projectId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => {
            const getDisplayValue = () => {
              const selectedProject = projects?.find(
                (project) => project.key === field.value,
              );
              const selectedProjectTitle = `(${selectedProject?.key}) ${selectedProject?.name}`;

              const noProjectFound = projectQuery.length > 0 && isProjectsEmpty;
              if (noProjectFound) {
                return <>No projects match the search criteria</>;
              }

              if (isProjectsEmpty) {
                return <>No projects available – create one in Jira first</>;
              }

              const notProjectSelected = !field.value || !selectedProjectTitle;
              if (notProjectSelected) {
                return <>Select a project</>;
              }

              return <>{selectedProjectTitle}</>;
            };

            return (
              <>
                <DropdownAutocomplete
                  open={isAutocompleteOpen}
                  anchorRef={anchorRef}
                  selected={getDisplayValue()}
                  onToggle={handleToggle}
                  data-testid="project-dropdown"
                />
                <Autocomplete
                  options={(projects || []).map(({ key, name }) => ({
                    value: key,
                    label: name,
                  }))}
                  scope="projects"
                  selected={field.value}
                  onSelect={(option) => {
                    saveSelectedProject(option);
                    field.onChange(option);
                    setIsAutocompleteOpen(false);
                  }}
                  open={isAutocompleteOpen}
                  onClose={() => setIsAutocompleteOpen(false)}
                  anchorRef={anchorRef}
                  onSearchInputChange={(e) => {
                    setProjectQuery(e.target.value);
                    fetchProjects(e.target.value);
                  }}
                />
              </>
            );
          }}
        />
      </FormControl>
      <FormControl fullWidth sx={{ marginTop: 3 }}>
        <CreateTicketTitleField
          textFieldProps={register("jira.title", { required: true })}
          errorMessage={formState.errors?.jira?.title?.message}
          disabled={!hasCreateCasePermissions}
        />
      </FormControl>
      <FormControl fullWidth sx={{ marginTop: 3 }}>
        <CreateTicketNotesField
          errorMessage={formState.errors.jira?.notes?.message}
          textFieldProps={register("jira.notes")}
          disabled={!hasCreateCasePermissions}
        />
      </FormControl>
    </>
  );
};
